import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { listarAsociadosConPisos } from '@lib/services/renaper.service';

/**
 * Modal con la lista de TODOS los asociados del padrón que tuvieron pisos
 * aplicados por Renaper. Se abre desde el banner del job tras un batch que
 * pisó campos. Cada fila muestra al titular/integrante + los campos pisados
 * (campo, valor anterior, valor nuevo).
 *
 * Visible solo desde el flow admin Medicus en PadronDetalle.
 */
interface Props {
  padronId: string;
  onClose: () => void;
}

const CAMPO_LABEL: Record<string, string> = {
  apellSolt: 'Apellido',
  nombres: 'Nombres',
  fechaNacimiento: 'Fecha de nacimiento',
};

export function PadronPisosModal({ padronId, onClose }: Props) {
  const q = useQuery({
    queryKey: ['padron-pisos', padronId],
    queryFn: () => listarAsociadosConPisos(padronId, { limit: 500 }),
    enabled: !!padronId,
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="padron-pisos-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 12,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          maxWidth: 980,
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.35)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-outline)',
          }}
        >
          <div>
            <h2
              id="padron-pisos-titulo"
              className="heading-5"
              style={{ margin: 0, color: 'var(--color-primary-darker)' }}
            >
              Campos pisados por Renaper
            </h2>
            <p
              className="text-element-mid"
              style={{ fontSize: 12, margin: '2px 0 0' }}
            >
              {q.data ? `${q.data.total.toLocaleString('es-AR')} asociados con pisos` : 'Cargando…'}
              {' '}— estos pasaron a estado{' '}
              <strong style={{ color: '#166534' }}>validado</strong> automáticamente.
            </p>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-element-mid)',
              padding: 4,
              display: 'flex',
            }}
          >
            <X size={20} />
          </button>
        </header>

        <div style={{ overflowY: 'auto', padding: '16px 20px', flex: '1 1 auto' }}>
          {q.isLoading && (
            <p className="text-element-mid" style={{ fontSize: 13 }}>Cargando lista…</p>
          )}
          {q.isError && (
            <p className="status status-error" style={{ display: 'block' }}>
              No se pudo cargar la lista de pisos.
            </p>
          )}
          {q.data && q.data.items.length === 0 && (
            <p className="text-element-mid" style={{ fontSize: 13 }}>
              No hay asociados con campos pisados en este padrón.
            </p>
          )}
          {q.data && q.data.items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {q.data.items.map((it) => (
                <div
                  key={it.asociadoId}
                  style={{
                    border: '1px solid var(--color-outline)',
                    borderRadius: 10,
                    padding: '10px 12px',
                    background: '#FAFAFA',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 8,
                      marginBottom: 8,
                      fontSize: 13,
                    }}
                  >
                    <strong style={{ color: 'var(--color-primary-darker)' }}>
                      Legajo {it.legajo || '—'} · {it.nombres} {it.apellSolt}
                    </strong>
                    <span style={{ color: 'var(--color-element-mid)' }}>
                      DNI {it.documento}
                    </span>
                  </div>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 12.5,
                      border: '1px solid var(--color-outline)',
                      background: 'white',
                    }}
                  >
                    <thead>
                      <tr style={{ background: '#F1F5F9' }}>
                        <th style={th}>Campo</th>
                        <th style={th}>Valor anterior (padrón)</th>
                        <th style={th}>Valor pisado (Renaper)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {it.pisos.map((p) => (
                        <tr key={`${it.asociadoId}-${p.campo}`}>
                          <td style={td}>{CAMPO_LABEL[p.campo] ?? p.campo}</td>
                          <td style={{ ...td, color: '#991B1B', textDecoration: 'line-through' }}>
                            {p.valorAnterior || '(vacío)'}
                          </td>
                          <td style={{ ...td, color: '#166534', fontWeight: 600 }}>
                            {p.valorNuevo || '(vacío)'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {q.data.total > q.data.items.length && (
                <p
                  className="text-element-mid"
                  style={{ fontSize: 12, textAlign: 'center', margin: 0 }}
                >
                  Mostrando los primeros {q.data.items.length} de {q.data.total}.
                </p>
              )}
            </div>
          )}
        </div>

        <footer
          style={{
            borderTop: '1px solid var(--color-outline)',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '6px 10px',
  textAlign: 'left',
  fontWeight: 700,
  borderBottom: '1px solid var(--color-outline)',
};
const td: React.CSSProperties = {
  padding: '6px 10px',
  borderTop: '1px solid var(--color-outline)',
};
